import { ElMessage, UploadUserFile } from 'element-plus'
import { OrganizationEditType } from '@/shared/types'
import { organizationsApi, userApi } from '@/shared/api'
import { getDateFormat, showErrorMessage } from '@/shared/utils'
import { useOrganizationListing, useUser } from '@/shared/composables'

const { updateOrganizationListing } = useOrganizationListing()
const { setUser, user } = useUser()

export const getOrganization = async (
  organizationId: number
): Promise<OrganizationEditType> => {
  const organization: OrganizationEditType = {
    address: '',
    blockDate: '',
    inn: '',
    isTest: 0,
    isBlocked: 0,
    logo: [],
    name: '',
    packageCount: 0,
    phone: '',
    site: '',
    modules: [],
    partner: null,
    status: null,
    tariffId: null,
    subscriptionType: null,
    tariff: null,
    useCustomTariff: false,
    customTariffData: null,
    dealDate: null,
    lastPaymentDate: null,
    telegramEnabled: null,
    telegramNotificationsEnabled: null,
    qrCodesEnabled: null,
  }

  const [error, response] = await organizationsApi.getOrganizationInfo(
    Number(organizationId)
  )

  if (error) {
    showErrorMessage(error)
  } else {
    const data = response.data.organization

    organization.name = data.name
    organization.address = data.address
    organization.phone = data.phone
    organization.site = data.site
    organization.inn = data.inn
    organization.status = data.status
    organization.partner = data.partner
    organization.isTest = data.is_test
    organization.isBlocked = data.is_blocked
    organization.blockDate = getDateFormat(
      new Date(data.block_at),
      'yyyy-MM-dd'
    )
    organization.packageCount = data.package_count

    if (data.logo) {
      organization.logo = getLogo(data.logo.id)
    }
    organization.modules = data.modules
    organization.tariffId = data.tariff ? data.tariff.id : null
    organization.subscriptionType = data.subscription_type
    organization.tariff = data.tariff
    organization.useCustomTariff = Boolean(data.tariff && data.tariff.is_custom)
    organization.customTariffData = data.custom_tariff_data ?? {
      report_count: 0,
      price_month: 0,
      price_over: 0,
      price_year: 0,
    }
    organization.dealDate = data.deal_date
    organization.lastPaymentDate = data.last_payment_date
    organization.telegramEnabled = data.telegram_enabled > 0
    organization.telegramNotificationsEnabled =
      data.telegram_notifications_enabled > 0
    organization.qrCodesEnabled = data.qr_codes_enabled > 0
  }

  return organization
}

export const editOrganization = async (
  organizationId: number,
  organization: OrganizationEditType
): Promise<boolean> => {
  const isOrganizationUpdated = await updateOrganization(
    organizationId,
    organization
  )

  if (!isOrganizationUpdated) return false

  if (!organization.logo.length) {
    const isLogoRemoved = await removeOrganizationLogo(organizationId)

    if (!isLogoRemoved) return false
  }

  updateOrganizationListing(true)

  if (organizationId === user.value.organization.id) {
    const [_, response] = await userApi.getInfoAboutMe()

    setUser(response.data)
  }

  ElMessage({
    message: 'Данные обновлены',
    type: 'success',
  })

  return true
}

const updateOrganization = async (
  organizationId: number,
  organization: OrganizationEditType
): Promise<boolean> => {
  const [error] = await organizationsApi.updateOrganization(
    organizationId,
    organization
  )

  if (error) {
    showErrorMessage(error)

    return false
  }

  return true
}

const removeOrganizationLogo = async (
  organizationId: number
): Promise<boolean> => {
  const [error] = await organizationsApi.removeOrganizationLogo(organizationId)

  if (error) {
    showErrorMessage(error)

    return false
  }

  return true
}

const getLogo = (id: number): UploadUserFile[] => {
  let domain = window.origin

  if (process.env.NODE_ENV === 'production') {
    domain = process.env.VUE_APP_BASE_URI
  }

  return [
    {
      name: 'logo.png',
      url: `${domain}/api/web/admin/reports/storage/${id}`,
    },
  ]
}
